import { useMemo } from 'react';
import styled from 'styled-components';

import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';
import { Disease, DiseaseQuery } from '@dreryk/medical-dictionaries-api';

import { CqrsQueryPerformResult, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import { FieldControllerProps, TranslatableText } from '@dreryk/react-common';
import { Autosuggest, fonts } from '@dreryk/react-components';
import { TextPattern } from '@dreryk/watermelon';

import { commonTranslationKeys } from '../../translations';

type DiagnosisSelectorProps = FieldControllerProps<'disease', Disease | null> & {
   readonly diagnoses: VisitDiagnosis[] | undefined;
   readonly required?: boolean;
   readonly label?: TranslatableText;
};

const DiagnosisSelectorDropdownPrimaryText = styled.div`
   font-weight: ${fonts.fontWeightBold};
   display: inline;
`;
DiagnosisSelectorDropdownPrimaryText.displayName = 'DiagnosisSelectorDropdownPrimaryText';

const diseaseLabelExtractor = (disease: Disease): string => {
   return disease.name;
};

const diseaseItemRenderer = (item: Disease): JSX.Element => {
   return (
      <div>
         <DiagnosisSelectorDropdownPrimaryText>{item.name}</DiagnosisSelectorDropdownPrimaryText>
         {` (${item.icd10Code})`}
      </div>
   );
};

const diseasesSearchCreator = (searchText: string | null) => {
   return async (): CqrsQueryPerformResult<Disease[]> =>
      performCqrsQuerySearch<Disease>(() => {
         const query = new DiseaseQuery();
         if (searchText) {
            query.nameOrIcd10Code = TextPattern.anywhereIgnoreCase(searchText);
         }
         return query;
      });
};

export const DiagnosisSelector = (props: DiagnosisSelectorProps): JSX.Element => {
   const defaultItems = useMemo<Disease[] | undefined>(() => {
      return props.diagnoses?.map((diagnosis) => ({
         icd10Code: diagnosis.icd10Code,
         id: diagnosis.id,
         name: diagnosis.name,
      }));
   }, [props.diagnoses]);

   return (
      <Autosuggest<Disease>
         minChars={2}
         keyProperty={'id'}
         placeholder={commonTranslationKeys.fillICD10OrDiseaseName}
         defaultItems={defaultItems}
         itemRenderer={diseaseItemRenderer}
         labelExtractor={diseaseLabelExtractor}
         controller={props.register(props.name, { required: props.required })}
         searchCreator={diseasesSearchCreator}
         testId='disease'
         label={props.label}
      />
   );
};
